import type { IgnoreModifier } from '@apollo/client/cache'

import { useAccount } from '~publish/contexts/AccountContext'
import { GetAccount } from '~publish/contexts/AccountContext/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { client } from '~publish/legacy/apollo-client'

export const UpdateAccountPreferences = graphql(`
  mutation UpdateAccountPreferences($input: UpdateAccountPreferencesInput!) {
    updateAccountPreferences(input: $input) {
      __typename
      ... on UpdateAccountPreferencesSuccess {
        preferences {
          timeFormat
          startOfWeek
          defaultScheduleOption
          landingPage
          community {
            mentionsCounter
          }
        }
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

type MutationResult = ResultOf<
  typeof UpdateAccountPreferences
>['updateAccountPreferences']

type UpdatedPreferences = Extract<
  NonNullable<MutationResult>,
  { __typename: 'UpdateAccountPreferencesSuccess' }
>

type Preferences = UpdatedPreferences['preferences']

const isSuccessFullResponse = (
  response: MutationResult | undefined,
): response is UpdatedPreferences => {
  return response?.__typename === 'UpdateAccountPreferencesSuccess'
}

/**
 * Custom hook to update and Accounts start of week preference mutation.
 * @returns {UpdateStartOfWeekPreferenceResponse} The function to trigger updating the start of week preference.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useUpdateAccountPreferences = () => {
  const { account } = useAccount()
  return useTypedMutation(
    UpdateAccountPreferences,
    (data) => data.updateAccountPreferences,
    {
      successTypename: 'UpdateAccountPreferencesSuccess',
      optimisticResponse: (
        variables,
        { IGNORE },
      ): ResultOf<typeof UpdateAccountPreferences> | IgnoreModifier => {
        const currentData = client.cache.readQuery({
          query: GetAccount,
        })

        const { defaultScheduleOption, timeFormat, startOfWeek, community } =
          currentData?.account?.preferences ?? {}

        if (!defaultScheduleOption || !timeFormat || !startOfWeek) {
          return IGNORE
        }

        // `landingPage` is not selected by `GetAccount` until the FE PR ships,
        // so we don't have a cached value to fall back to. The mutation input
        // carries the new value when the user changes it; otherwise we
        // optimistically default to 'home' (the server response immediately
        // overrides this).
        const updatedPreferences: Preferences = {
          defaultScheduleOption:
            variables.input.defaultScheduleOption ?? defaultScheduleOption,
          timeFormat: variables.input.timeFormat ?? timeFormat,
          startOfWeek: variables.input.startOfWeek ?? startOfWeek,
          landingPage: variables.input.landingPage ?? 'home',
          community: {
            mentionsCounter:
              variables.input.mentionsCounter ??
              community?.mentionsCounter ??
              'counter',
          },
        }

        return {
          updateAccountPreferences: {
            __typename: 'UpdateAccountPreferencesSuccess',
            preferences: updatedPreferences,
          },
        }
      },
      update: (cache, { data }) => {
        const result = data?.updateAccountPreferences
        if (isSuccessFullResponse(result) && account?.id != null) {
          const cacheId = cache.identify({
            __typename: 'Account',
            id: String(account.id),
          })

          if (cacheId) {
            cache.modify({
              id: cacheId,
              fields: {
                preferences() {
                  return {
                    __typename: 'Preferences',
                    timeFormat: result.preferences.timeFormat ?? '24',
                    startOfWeek: result.preferences.startOfWeek ?? 'monday',
                    defaultScheduleOption:
                      result.preferences.defaultScheduleOption ?? 'Queue',
                    landingPage: result.preferences.landingPage ?? 'home',
                    community: {
                      __typename: 'CommunityPreferences',
                      mentionsCounter:
                        result.preferences.community?.mentionsCounter ??
                        'counter',
                    },
                  }
                },
              },
            })
          }
        }
      },
    },
  )
}
